import type { CollectionDefaults, CollectionRelation, CollectionRelationReference, ResolvedCollection, StoreCore, StoreSchema } from '@rstore/shared';
import type { AnyItem, CompositePrimaryKey, ConnectToMany, CreateToMany, CreateToOne, DeleteToMany, DisconnectToMany, PrimitivePrimaryKey, RelationOperationToMany, RelationOperationToOne, UpdateToMany, UpdateToOne } from '#layers/data-model';
import { isConnectToMany, isConnectToOne, isCreateToMany, isCreateToOne, isDeleteToMany, isDisconnectToMany, isDisconnectToOne, isFilterOperator, isUpdateToMany, isUpdateToOne } from '#layers/data-model';

function resolveRelationRef(
	ref: CollectionRelationReference | CollectionRelationReference[] | undefined,
): CollectionRelationReference[] {
	if (!ref) return [];
	return Array.isArray(ref) ? ref : [ref];
}

/**
 * Field-name pairs from a relation's `on` map. The rstore type widens the key/value to
 * `string | number | symbol`, but relation `on` maps are always field-name → field-name, so
 * this narrows them to `[string, string]` once at the boundary instead of casting per use.
 */
function relationFieldPairs(on: CollectionRelationReference['on']): [string, string][] {
	return Object.entries(on) as [string, string][];
}

/**
 * Checks if a value is any relation operation.
 */
export function isRelationOperation(value: unknown) {
	if (!value || typeof value !== 'object') return false;
	return '_create' in value || '_connect' in value || '_update' in value
		|| '_delete' in value || '_disconnect' in value;
}

/**
 * Gets the relation definition for a field.
 */
export function getRelation(
	collection: ResolvedCollection,
	fieldName: string,
): CollectionRelation | null {
	const relations = collection.relations;
	return relations?.[fieldName] ?? null;
}

/**
 * Finds the target collection for a relation.
 */
export function findTargetCollection(
	collections: ResolvedCollection[],
	relation: CollectionRelation,
): ResolvedCollection | null {
	for (const collectionKey in relation.to) {
		const collection = collections.find((c) => c.name === collectionKey);
		if (collection) return collection;
	}

	return null;
}

/**
 * Extracts PK fields from item data as CompositePrimaryKey for recursive calls.
 */
export function extractPKFromItem(
	item: AnyItem,
	collection: ResolvedCollection,
): CompositePrimaryKey {
	const pk: CompositePrimaryKey = {};

	const pkFields = collection.meta?.primaryKeyFields ?? ['id'];

	for (const field of pkFields) {
		if (item[field] !== undefined) {
			pk[field] = item[field];
		}
	}

	return pk;
}

/**
 * Converts a filter to a cache key.
 */
export function filterToKey(
	filter: Record<string, unknown> | CompositePrimaryKey,
	collection: ResolvedCollection,
): PrimitivePrimaryKey | null {
	const filterKeys = Object.keys(filter);

	// Check if any top-level key is a filter operator (_and, _or, _not)
	const hasTopLevelOperator = filterKeys.some((k) => isFilterOperator(k));
	if (hasTopLevelOperator) return null;

	// Check if any value is an object containing filter operators
	const hasNestedOperator = filterKeys.some((k) => {
		const value = filter[k];

		if (value && typeof value === 'object' && !Array.isArray(value)) {
			return Object.keys(value).some((nestedKey) => isFilterOperator(nestedKey));
		}

		return false;
	});
	if (hasNestedOperator) return null;

	return collection.getKey(filter as CompositePrimaryKey) ?? null;
}

/**
 * Extracts FK values from parent for to-many relations.
 */
export function extractParentFK(
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
): CompositePrimaryKey {
	if (!parentPK) return {};

	const fks: CompositePrimaryKey = {};
	const refs = resolveRelationRef(relation.to[targetCollection.name]);

	for (const ref of refs) {
		if (!ref.on) continue;

		for (const [fkField, pkField] of relationFieldPairs(ref.on)) {
			const value = parentPK[pkField];

			if (value !== undefined) {
				fks[fkField] = value;
			}
		}
	}

	return fks;
}

/**
 * Extracts FK values from to-one _connect and _create operations.
 */
export function extractToOneFKs(
	payload: AnyItem,
	collection: ResolvedCollection,
): AnyItem {
	const fks: AnyItem = {};

	for (const [fieldName, value] of Object.entries(payload)) {
		const relation = getRelation(collection, fieldName);
		if (!relation || relation.many) continue;

		const ops = Array.isArray(value) ? value : [value];

		for (const op of ops) {
			if (!isRelationOperation(op)) continue;

			let sourceObject: AnyItem | null = null;
			let isNullify = false;

			if (isConnectToOne(op)) {
				sourceObject = op._connect.key;
			}
			else if (isCreateToOne(op)) {
				sourceObject = op._create.data;
			}
			else if (isDisconnectToOne(op)) {
				isNullify = true;
			}

			if (!sourceObject && !isNullify) continue;

			const refs = resolveRelationRef(Object.values(relation.to)[0]);

			for (const ref of refs) {
				if (!ref.on) continue;

				for (const [targetField, localFkField] of relationFieldPairs(ref.on)) {
					if (isNullify) {
						fks[localFkField] = null;
					}
					else if (sourceObject) {
						const val = sourceObject[targetField];

						if (val !== undefined) {
							fks[localFkField] = val;
						}
					}
				}
			}
		}
	}

	return fks;
}

/**
 * Returns FK fields set to null for disconnect operations.
 */
export function extractNullFK(
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
): AnyItem {
	const fks: AnyItem = {};
	const refs = resolveRelationRef(relation.to[targetCollection.name]);

	for (const ref of refs) {
		if (!ref.on) continue;

		for (const fkField of Object.keys(ref.on)) {
			fks[fkField] = null;
		}
	}

	return fks;
}

/**
 * Builds item data for cache write by:
 * 1. Copying primitive fields
 * 2. Adding FK from parent (for to-many relations)
 * 3. Extracting FKs from to-one _connect/_create operations
 * 4. Nullifying FKs from to-one _disconnect operations
 */
export function buildItemData(
	payload: AnyItem,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
): AnyItem {
	const result: AnyItem = {};

	for (const [field, value] of Object.entries(payload)) {
		if (value === null || value === undefined) {
			result[field] = value;
			continue;
		}

		if (Array.isArray(value)) continue;
		if (typeof value === 'object' && isRelationOperation(value)) continue;

		if (typeof value !== 'object') {
			result[field] = value;
		}
		else {
			const fieldRelation = getRelation(targetCollection, field);

			if (!fieldRelation) {
				result[field] = value;
			}
		}
	}

	if (parentPK) {
		Object.assign(result, extractParentFK(relation, targetCollection, parentPK));
	}

	Object.assign(result, extractToOneFKs(payload, targetCollection));

	return result;
}

interface CacheWriteOp {
	type: 'WRITE';
	collection: ResolvedCollection;
	key: PrimitivePrimaryKey;
	data: AnyItem;
}

interface CacheDeleteOp {
	type: 'DELETE';
	collection: ResolvedCollection;
	key: PrimitivePrimaryKey;
}

type CacheOp = CacheWriteOp | CacheDeleteOp;

/**
 * Collects all cache operations from a payload (recursive).
 */
export function collectCacheOperations(
	collections: ResolvedCollection[],
	payload: AnyItem,
	collection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited: Set<string> = new Set(),
): CacheOp[] {
	const ops: CacheOp[] = [];

	// Circular reference detection
	const visitKey = `${collection.name}:${JSON.stringify(parentPK ?? {})}`;

	if (visited.has(visitKey)) {
		console.warn('[applyRelationOperationsToCache] Circular reference detected:', {
			collection: collection.name,
			parentPK,
		});

		return ops;
	}

	visited.add(visitKey);

	for (const [fieldName, value] of Object.entries(payload)) {
		if (value === null || value === undefined) continue;

		const relation = getRelation(collection, fieldName);
		if (!relation) continue;

		const targetCollection = findTargetCollection(collections, relation);
		if (!targetCollection) continue;

		if (relation.many) {
			ops.push(...processToManyOperations(collections, value as RelationOperationToMany[], relation, targetCollection, parentPK, visited));
		}
		else if (Array.isArray(value)) {
			// To-one in update context: array of SingleToOneOperation
			for (const op of value) {
				ops.push(...processToOneOperation(collections, op as RelationOperationToOne, relation, targetCollection, parentPK, visited));
			}
		}
		else if (typeof value === 'object') {
			ops.push(...processToOneOperation(collections, value, relation, targetCollection, parentPK, visited));
		}
	}

	return ops;
}

function processToManyOperations(
	collections: ResolvedCollection[],
	operations: RelationOperationToMany[],
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];

	for (const operation of operations) {
		if (isCreateToMany(operation)) {
			ops.push(...processCreateToMany(collections, operation, relation, targetCollection, parentPK, visited));
		}
		else if (isConnectToMany(operation)) {
			ops.push(...processConnectToMany(operation, relation, targetCollection, parentPK));
		}
		else if (isUpdateToMany(operation)) {
			ops.push(...processUpdateToMany(collections, operation, relation, targetCollection, visited));
		}
		else if (isDeleteToMany(operation)) {
			ops.push(...processDeleteToMany(operation, targetCollection));
		}
		else if (isDisconnectToMany(operation)) {
			ops.push(...processDisconnectToMany(operation, relation, targetCollection));
		}
		else {
			console.error('[applyRelationOperationsToCache] Unknown to-many operation:', operation);
		}
	}

	return ops;
}

function processCreateToMany(
	collections: ResolvedCollection[],
	operation: CreateToMany,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];

	for (const itemPayload of operation._create.data) {
		const itemData = buildItemData(itemPayload, relation, targetCollection, parentPK);

		const key = targetCollection.getKey(itemData);

		if (!key) {
			console.error('[applyRelationOperationsToCache] Failed to generate key for create:', {
				collection: targetCollection.name,
				itemData,
			});

			continue;
		}

		ops.push({ type: 'WRITE', collection: targetCollection, key, data: itemData });

		const itemPK = extractPKFromItem(itemData, targetCollection);
		ops.push(...collectCacheOperations(collections, itemPayload, targetCollection, itemPK, visited));
	}

	return ops;
}

function processConnectToMany(
	operation: ConnectToMany,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
): CacheOp[] {
	const ops: CacheOp[] = [];
	const fkData = extractParentFK(relation, targetCollection, parentPK);

	for (const connectKey of operation._connect.keys) {
		const key = targetCollection.getKey(connectKey);

		if (!key) {
			console.error('[applyRelationOperationsToCache] Failed to generate key for connect:', {
				collection: targetCollection.name,
				connectKey,
			});

			continue;
		}

		ops.push({ type: 'WRITE', collection: targetCollection, key, data: fkData });
	}

	return ops;
}

function processUpdateToMany(
	collections: ResolvedCollection[],
	operation: UpdateToMany,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];

	const { filter, data } = operation._update;
	if (!filter) return ops;

	const key = filterToKey(filter as Record<string, unknown>, targetCollection);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Cannot resolve key from update filter:', {
			collection: targetCollection.name,
			filter,
		});

		return ops;
	}

	const updateData = buildItemData(data, relation, targetCollection);

	if (Object.keys(updateData).length > 0) {
		ops.push({ type: 'WRITE', collection: targetCollection, key, data: updateData });
	}

	ops.push(...collectCacheOperations(collections, data, targetCollection, filter as CompositePrimaryKey, visited));

	return ops;
}

function processDeleteToMany(
	operation: DeleteToMany,
	targetCollection: ResolvedCollection,
): CacheOp[] {
	const { filter } = operation._delete;
	const key = filterToKey(filter as Record<string, unknown>, targetCollection);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Cannot resolve key from delete filter:', {
			collection: targetCollection.name,
			filter,
		});

		return [];
	}

	return [{ type: 'DELETE', collection: targetCollection, key }];
}

function processDisconnectToMany(
	operation: DisconnectToMany,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
): CacheOp[] {
	const { filter } = operation._disconnect;
	const key = filterToKey(filter as Record<string, unknown>, targetCollection);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Cannot resolve key from disconnect filter:', {
			collection: targetCollection.name,
			filter,
		});

		return [];
	}

	const nullFKs = extractNullFK(relation, targetCollection);

	return [{ type: 'WRITE', collection: targetCollection, key, data: nullFKs }];
}

function processToOneOperation(
	collections: ResolvedCollection[],
	op: RelationOperationToOne,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited?: Set<string>,
): CacheOp[] {
	if (isConnectToOne(op)) {
		return [];
	}

	if (isCreateToOne(op)) {
		return processCreateToOne(collections, op, relation, targetCollection, parentPK, visited);
	}

	if (isUpdateToOne(op)) {
		return processUpdateToOne(collections, op, relation, targetCollection, visited);
	}

	if (isDisconnectToOne(op)) {
		return [];
	}

	console.error('[applyRelationOperationsToCache] Unknown to-one operation:', op);
	return [];
}

function processCreateToOne(
	collections: ResolvedCollection[],
	operation: CreateToOne,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];
	const itemPayload = operation._create.data;

	const itemData = buildItemData(itemPayload, relation, targetCollection, parentPK);

	const key = targetCollection.getKey(itemData);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Failed to generate key for to-one create:', {
			collection: targetCollection.name,
			itemData,
		});

		return ops;
	}

	ops.push({ type: 'WRITE', collection: targetCollection, key, data: itemData });

	const itemPK = extractPKFromItem(itemData, targetCollection);
	ops.push(...collectCacheOperations(collections, itemPayload, targetCollection, itemPK, visited));

	return ops;
}

function processUpdateToOne(
	collections: ResolvedCollection[],
	operation: UpdateToOne,
	relation: CollectionRelation,
	targetCollection: ResolvedCollection,
	visited?: Set<string>,
): CacheOp[] {
	const ops: CacheOp[] = [];

	const { filter, data } = operation._update;

	if (!filter) {
		console.error('[applyRelationOperationsToCache] UpdateToOne missing filter:', operation);
		return ops;
	}

	const key = filterToKey(filter as Record<string, unknown>, targetCollection);

	if (!key) {
		console.error('[applyRelationOperationsToCache] Cannot resolve key from to-one update filter:', {
			collection: targetCollection.name,
			filter,
		});

		return ops;
	}

	const updateData = buildItemData(data, relation, targetCollection);

	if (Object.keys(updateData).length > 0) {
		ops.push({ type: 'WRITE', collection: targetCollection, key, data: updateData });
	}

	ops.push(...collectCacheOperations(collections, data, targetCollection, filter as CompositePrimaryKey, visited));

	return ops;
}

/**
 * Main entry point. Processes a payload and applies all cache operations.
 */
export function applyRelationOperationsToCache<
	TStore extends StoreCore<TSchema, TCollectionDefaults>,
	TSchema extends StoreSchema,
	TCollectionDefaults extends CollectionDefaults,
>(
	store: TStore,
	payload: AnyItem,
	collection: ResolvedCollection,
	parentPK?: CompositePrimaryKey,
) {
	const ops = collectCacheOperations(store.$collections, payload, collection, parentPK);

	for (const op of ops) {
		if (op.type === 'WRITE') {
			store.$cache.writeItem({
				collection: op.collection,
				key: op.key,
				item: op.data,
			});
		}
		else {
			store.$cache.deleteItem({
				collection: op.collection,
				key: op.key,
			});
		}
	}
}
